import { Box, MenuItem, SelectChangeEvent, Stack, styled } from '@mui/material';
import { EditIcon, UsersIcon } from '@synergy/theme-next';
import { FormattedMessage } from 'react-intl';
import { MouseEvent, ReactElement } from 'react';
import { PlusCircleIcon } from '@synergy/theme';
import {
    PoolButton,
    PoolIconButton,
    PoolRow,
    PoolSelect,
    PoolSelectOption,
    PoolTypography,
} from '@synergy/components-next';
import { shouldForwardPropExclude } from '@synergy/utils';

import { WishListId, WishListListItem } from '../../models/wish-list-list-item';
import { WishListType } from '../../models/enums/wish-list-type';

const StyledUsersIcon = styled(UsersIcon, { shouldForwardProp: shouldForwardPropExclude('isSmall') })<{
    isSmall: boolean;
}>(({ theme, isSmall }) => ({
    borderRadius: 16,
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[800],
    width: isSmall ? 20 : 24,
    height: isSmall ? 20 : 24,
    padding: '2px',
}));

const DisplayWishList = ({ list, isSmall }: { list: WishListListItem; isSmall?: boolean }): ReactElement => (
    <Stack direction="row" gap={1} alignItems="center">
        <StyledUsersIcon isSmall={!!isSmall} />
        <Box>
            {list.name} ({list.isOwner ? <FormattedMessage id="app.wishList.sharedMyself" /> : list.owner?.fullName})
        </Box>
    </Stack>
);

export interface WishListDropdownProps {
    wishLists: WishListListItem[];
    value?: WishListId;
    onChange?: (id: WishListId) => void;
    onAddWishList?: () => void;
    onEditWishList?: (id: WishListId) => void;
}
export const WishListDropdown = (props: WishListDropdownProps): ReactElement => {
    const { wishLists, value, onChange, onAddWishList, onEditWishList } = props;

    const options = wishLists?.map<PoolSelectOption<WishListId>>(list => ({
        value: list.id,
        displayValue:
            !list.isSharedWithOtherAccountUsers || list.isDefault ? (
                list.name
            ) : (
                <DisplayWishList list={list} isSmall={true} />
            ),
    }));

    const handleListChange = (e: SelectChangeEvent<'' | WishListId>): void => {
        if (typeof e.target.value === 'number' || e.target.value === 'default') {
            onChange?.(e.target.value);
        }
    };

    const handleClick =
        (id: WishListId | '') =>
        (e: MouseEvent): void => {
            e.preventDefault();
            e.stopPropagation();
            onEditWishList?.(id || -1);
        };

    const notSharedWishLists = wishLists.filter(list => !list.isSharedWithOtherAccountUsers || list.isDefault);
    const sharedWishLists = wishLists.filter(list => list.isSharedWithOtherAccountUsers && !list.isDefault);

    return (
        <PoolSelect<WishListId>
            fullWidth={true}
            options={options}
            value={value}
            onChange={handleListChange}
            label={<FormattedMessage id="app.wishList.label" />}
        >
            {notSharedWishLists.map(list => (
                <MenuItem key={list.id} value={list.id}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                        <Box>{list.name}</Box>
                        {!list.isDefault && (
                            <PoolIconButton color="dark" onClick={handleClick(list.id)}>
                                <EditIcon />
                            </PoolIconButton>
                        )}
                    </Stack>
                </MenuItem>
            ))}
            {sharedWishLists.length > 0 && (
                <MenuItem disabled={true} sx={{ opacity: '1 !important' }}>
                    <PoolTypography typography="statusLabel" translationId="app.wishList.sharedLists" />
                </MenuItem>
            )}
            {sharedWishLists.map(list => (
                <MenuItem key={list.id} value={list.id}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                        <DisplayWishList list={list} />
                        {(list.type === WishListType.OrganizationEdit || list.isOwner) && (
                            <PoolIconButton color="dark" onClick={handleClick(list.id)}>
                                <EditIcon />
                            </PoolIconButton>
                        )}
                    </Stack>
                </MenuItem>
            ))}
            <Box sx={{ width: '100%', height: 48, px: 2 }}>
                <PoolRow gap={0} justifyContent="end" color="primary.main" sx={{ width: '100%', height: '100%' }}>
                    <PoolButton variant="text" startIcon={<PlusCircleIcon />} onClick={onAddWishList}>
                        <FormattedMessage id="app.wishList.add" />
                    </PoolButton>
                </PoolRow>
            </Box>
        </PoolSelect>
    );
};
